import FiltersSortHeaderStaticColumn from "components/Filters/SortHeader/StaticColumn";
import FiltersSortHeaderWrapper from "components/Filters/SortHeader/Wrapper";
import Box from "ds/components/Box";
import Toggle from "ds/components/Toggle";
import Typography from "ds/components/Typography";
import { isRunReviewable } from "shared/Run/useReviewRun/accessValidation";
import { Run, Stack } from "types/generated";

import { COLUMN_GAP } from "../constants";
import { getGroupKey, getGroupPushCommits, getRunsColumnOrder } from "../helpers";
import RunElement from "../RunElement";
import RunsGroup from "../RunsGroup";
import styles from "./styles.module.css";

type RunsCommitGroupProps = {
  hash: string;
  group: Run[];
  selectable: boolean;
  index: number;
  stack: Stack;
  isAnsible: boolean;
  selectedSet: Set<string>;
  fullySelectedGroups: Set<string>;
  onItemCheck: (item: Run, newChecked: boolean) => void;
  onGroupSelect: (groups: Run[], groupKey: string) => void;
};

const RunsCommitGroup = ({
  hash,
  group,
  selectable,
  index,
  stack,
  isAnsible,
  selectedSet,
  fullySelectedGroups,
  onItemCheck,
  onGroupSelect,
}: RunsCommitGroupProps) => {
  const someRunsShouldBeApproved = group.some(isRunReviewable);
  const groupKey = getGroupKey(hash, index);

  return (
    <RunsGroup
      someRunsShouldBeApproved={someRunsShouldBeApproved}
      run={group[0]}
      provider={stack.provider}
      count={group.length}
      driftDetectedCount={group.filter((groupRun) => groupRun.driftDetection).length}
      pushCommits={getGroupPushCommits(group)}
      isOpen
    >
      <Box direction="column" className={styles.tableScrollableWrapper}>
        <Box
          direction="column"
          className={styles.tableInnerWrapper}
          aria-label={group[0].title}
          aria-colcount={selectable ? 8 : 7}
          role="table"
        >
          <FiltersSortHeaderWrapper
            columnOrder={getRunsColumnOrder(selectable, isAnsible)}
            columnGap={COLUMN_GAP}
            ariaRole="row"
          >
            {selectable && (
              <FiltersSortHeaderStaticColumn role="columnheader">
                <Toggle
                  variant="checkbox"
                  id={groupKey}
                  onChange={() => onGroupSelect(group, groupKey)}
                  isSelected={fullySelectedGroups.has(groupKey)}
                  tooltip={() =>
                    fullySelectedGroups.has(groupKey)
                      ? "Unselect all runs for this commit"
                      : "Select all runs for this commit"
                  }
                  aria-label="Select all runs for this commit"
                />
              </FiltersSortHeaderStaticColumn>
            )}
            <FiltersSortHeaderStaticColumn role="columnheader">
              <Typography tag="span" variant="p-t7">
                Status
              </Typography>
            </FiltersSortHeaderStaticColumn>
            <FiltersSortHeaderStaticColumn role="columnheader">
              <Typography tag="span" variant="p-t7">
                Date
              </Typography>
            </FiltersSortHeaderStaticColumn>
            <FiltersSortHeaderStaticColumn role="columnheader">
              <Typography tag="span" variant="p-t7">
                Triggered by
              </Typography>
            </FiltersSortHeaderStaticColumn>
            <FiltersSortHeaderStaticColumn role="columnheader">
              <Typography tag="span" variant="p-t7">
                Version
              </Typography>
            </FiltersSortHeaderStaticColumn>
            <FiltersSortHeaderStaticColumn role="columnheader">
              <Typography tag="span" variant="p-t7">
                Delta
              </Typography>
            </FiltersSortHeaderStaticColumn>
            <FiltersSortHeaderStaticColumn role="columnheader">
              <Typography tag="span" variant="p-t7">
                Drift detected
              </Typography>
            </FiltersSortHeaderStaticColumn>
          </FiltersSortHeaderWrapper>
          {group.map((run) => (
            <RunElement
              selectable={selectable}
              key={run.id}
              stackId={stack.id}
              vendorConfig={stack.vendorConfig || undefined}
              run={run}
              checked={selectedSet.has(run.id)}
              onCheck={onItemCheck}
              hash={hash}
              isAnsible={isAnsible}
            />
          ))}
        </Box>
      </Box>
    </RunsGroup>
  );
};

export default RunsCommitGroup;
